import { Button } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';
import {
  PROMPT_BANNER_MAX_WIDTH,
  PromptBannerCard,
  MintListingPromptPattern,
  mintListingPromptBannerLayout,
  mintListingPromptBodyTextProps,
  mintListingPromptTitleTextProps,
  promptBannerPillButtonStyle,
  useMintListingPromptMinHeight,
  promptBannerV2NeedButtonStyle,
  usePromptBannerCappedContentWidth,
} from 'app/components/prompt-banner-card';
import { useNavigation } from 'app/hooks/navigation';
import { useAuthorize } from 'app/hooks/use-authorize';
import { useOpenOffer } from 'app/hooks/use-open-offer';
import { useOpenNewWithGuard } from 'app/hooks/use-open-new';
import { type NewListingSource, useTrackNewListingSource } from 'app/network/listing';
import { ListingType } from 'app/types/listing';
import { navigateToQuickPublishFlow } from 'app/screens/intro/navigator-helper';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { Trans, useTranslation } from 'react-i18next';
import type { ViewProps } from 'react-native';
import { Pressable, View } from 'react-native';
import { Column, Row } from 'app/design/primitives/box';
import { useUISize } from 'app/utils/web-helpers';
import { useUser } from 'app/network/user';

import { createTheme as createDSTheme } from 'app/shopify';
import { IconPlus } from '@tabler/icons-react-native';
const theme = createDSTheme();

function PostOfferPromptCard({
  title,
  text,
  buttonText,
  newListingSource,
  quick,
  request,
  style,
  /** @deprecated Ignored; banner width is capped by the shared shell (400px max). */
  width: _width,
  skipWidth,
  v2,
  hideStats,
  ...rest
}: ViewProps & {
  title: string;
  text?: string;
  buttonText: string;
  newListingSource: NewListingSource;
  quick?: boolean;
  request?: boolean;
  width?: number;
  skipWidth?: boolean;
  v2?: boolean;
  hideStats?: boolean;
}) {
  const navigation = useNavigation();
  const { data: user, isPending: isUserPending } = useUser();
  const authorize = useAuthorize();
  const trackNewListingSource = useTrackNewListingSource();
  const minHeight = useMintListingPromptMinHeight();
  const { t } = useTranslation('all-listings');

  const openNewWithGuard = useOpenNewWithGuard();
  const openOffer = useOpenOffer();
  const addOffer = () => {
    trackNewListingSource(newListingSource);
    if (quick) {
      authorize(() => navigateToQuickPublishFlow(navigation, user?.featureFlags, isUserPending));
    } else if (request) {
      openNewWithGuard(navigation, { defaultValues: { type: ListingType.request } });
    } else {
      openOffer(navigation, { newListingSource });
    }
  };

  const isSmallUI = useUISize().isSmallUI;
  const v2ButtonWidth = usePromptBannerCappedContentWidth();

  if (v2) {
    return (
      <View {...rest} style={style}>
        <Column
          alignItems="flex-start"
          alignSelf={isSmallUI ? 'stretch' : 'flex-start'}
          gap="spacing.general.x8"
          maxWidth={isSmallUI ? undefined : PROMPT_BANNER_MAX_WIDTH}
          width="100%"
        >
          <Button
            onPress={addOffer}
            style={[
              promptBannerV2NeedButtonStyle(v2ButtonWidth, ListingType.offer),
              isSmallUI ? { width: '100%' } : null,
            ]}
          >
            <Row alignItems="center" justifyContent="space-between" width="100%">
              <Row alignItems="center" gap="spacing.general.x4">
                <Text fontSize={24} fontWeight="700">
                  🫴
                </Text>
                <Text color="text.primary" fontWeight="700" variant="bodyLarge">
                  {t(buttonText)}
                </Text>
              </Row>
              <IconPlus strokeWidth={ICON_STROKE_WIDTH} size={30} color={theme.colors['text.primary']} />
            </Row>
          </Button>

          {!hideStats ? (
            <Row alignItems="center" gap="spacing.general.x4">
              <Text fontSize={39} fontWeight="700">
                {t(title)}
              </Text>
              <Text flex={1} variant="bodyLarge">
                <Trans components={{ strong: <Text fontWeight="700" /> }} i18nKey={text} ns="all-listings" />
              </Text>
            </Row>
          ) : null}
        </Column>
      </View>
    );
  }

  return (
    <Pressable
      {...rest}
      onPress={addOffer}
      style={skipWidth ? { flex: 1, maxWidth: PROMPT_BANNER_MAX_WIDTH, minWidth: 0 } : undefined}
    >
      <PromptBannerCard
        {...mintListingPromptBannerLayout}
        backgroundDecoration={<MintListingPromptPattern />}
        style={[{ minHeight }, skipWidth ? { alignSelf: 'stretch', width: '100%' } : null, style]}
      >
        <Text {...mintListingPromptTitleTextProps}>{t(title)}</Text>
        <Text {...mintListingPromptBodyTextProps}>
          {text ?? (
            <Trans
              components={{ strong: <Text fontWeight="700" /> }}
              i18nKey="postOfferPrompt.first.text"
              ns="all-listings"
            />
          )}
        </Text>
        <Button onPress={addOffer} style={promptBannerPillButtonStyle} variant="small-pill">
          <Text color="surface.default" variant="bodyBase">
            {t(buttonText)}
          </Text>
        </Button>
      </PromptBannerCard>
    </Pressable>
  );
}

export { PostOfferPromptCard };
